import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Form,
} from "react-bootstrap";
import {
  Input,
  Button as AntButton,
  Upload,
  Space,
  message,
  Divider,
} from "antd";
import {
  UploadOutlined,
  PaperClipOutlined,
  MailOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { FaReply, FaReplyAll, FaShare } from "react-icons/fa";
import { API_BASE_URL, Assets_URL } from "../../../Apicongfig"; // Adjust path as needed
import { useTranslation } from "react-i18next";
import axios from "axios";
import MessageTypeWritter from "../MessageTypeWritter"; // Adjust path as needed
import "bootstrap/dist/css/bootstrap.min.css";
import "antd/dist/reset.css";
import Moments from "../Moments";
import { Link } from "react-router-dom";
import DiscussionParticipant from "../DiscussionParticipant";

function OutlookChat() {
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState({ emails: true, emailContent: true });
  const [attachments, setAttachments] = useState([]);
  const [composingEmail, setComposingEmail] = useState(false);
  const [replyToEmail, setReplyToEmail] = useState(null);
  const [forwardEmail, setForwardEmail] = useState(null);
  const { t } = useTranslation("global");
  const editorRef = useRef(null);
  const messageContainerRef = useRef(null);

  // Sync emails
  const syncEmails = async () => {
    try {
      setLoading((prev) => ({ ...prev, emails: true }));
      const response = await axios.get(`${API_BASE_URL}/outlook-emails/sync`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
      });
      // setEmails(response.data.data || []);
    } catch (error) {
      console.error("Error syncing emails:", error);
    } finally {
      setLoading((prev) => ({ ...prev, emails: false }));
    }
  };

  // Fetch emails
  useEffect(() => {
    const fetchEmails = async () => {
      try {
        setLoading((prev) => ({ ...prev, emails: true }));
        const response = await axios.get(`${API_BASE_URL}/outlook-emails`, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        });
        setEmails(response.data.data?.data || []);
      } catch (error) {
        console.error("Error fetching emails:", error);
      } finally {
        setLoading((prev) => ({ ...prev, emails: false }));
      }
    };

    fetchEmails();
    syncEmails();
  }, []);

  const handleSelectEmail = async (email) => {
    setSelectedEmail(email);
    setLoading((prev) => ({ ...prev, emailContent: true }));
    try {
      if (!email.is_read) {
        await axios.post(
          `${API_BASE_URL}/outlook-emails/${email.id}/mark-as-read`,
          null,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setEmails((prev) =>
          prev.map((e) => (e.id === email.id ? { ...e, is_read: true } : e))
        );
      }
    } catch (error) {
      console.error("Error marking email as read:", error);
    } finally {
      setLoading((prev) => ({ ...prev, emailContent: false }));
    }
  };

  const handleSendEmail = async (emailContent) => {
    try {
      const formData = new FormData();
      formData.append("subject", emailContent.subject);
      formData.append("body", emailContent.message);

      // Split and append 'to' emails as array of objects
      const toEmails = emailContent.to
        .split(",")
        .map((e) => e.trim())
        .filter((e) => e);
      toEmails.forEach((email, index) => {
        formData.append(`to[${index}][email]`, email);
      });

      // Split and append 'cc' emails as array of objects, if provided
      if (emailContent.cc) {
        const ccEmails = emailContent.cc
          .split(",")
          .map((e) => e.trim())
          .filter((e) => e);
        ccEmails.forEach((email, index) => {
          formData.append(`cc[${index}][email]`, email);
        });
      }

      // Split and append 'bcc' emails as array of objects, if provided
      if (emailContent.bcc) {
        const bccEmails = emailContent.bcc
          .split(",")
          .map((e) => e.trim())
          .filter((e) => e);
        bccEmails.forEach((email, index) => {
          formData.append(`bcc[${index}][email]`, email);
        });
      }

      // Append attachments
      attachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });

      const response = await axios.post(
        `${API_BASE_URL}/outlook-emails/send`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        setEmails((prev) => [response.data.data, ...prev]);
        setComposingEmail(false);
        setReplyToEmail(null);
        setForwardEmail(null);
        setAttachments([]);
      }
    } catch (error) {
      console.error("Error sending email:", error);
      message.error(
        t(
          "Failed to send email: " +
            (error.response?.data?.message || "Unknown error")
        )
      );
    }
  };

  const handleFileUpload = (file) => {
    setAttachments((prev) => [...prev, file]);
    return false; // Prevent automatic upload
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleReply = (email, replyAll = false) => {
    setReplyToEmail({ ...email, replyAll });
    setComposingEmail(true);
    if (editorRef.current) {
      editorRef.current.setContent(
        `<br><br>-------- Original Message --------<br>` +
          `<strong>From:</strong> ${email.from_name} &lt;${email.from_email}&gt;<br>` +
          `<strong>Date:</strong> ${new Date(
            email.sent_date
          ).toLocaleString()}<br>` +
          `<strong>Subject:</strong> ${email.subject}<br>` +
          `<strong>To:</strong> ${email.to_recipients
            .map((r) => r.address)
            .join(", ")}<br>` +
          (email.cc_recipients.length
            ? `<strong>Cc:</strong> ${email.cc_recipients
                .map((r) => r.address)
                .join(", ")}<br>`
            : "") +
          `<br>${email.body_content}`
      );
    }
  };

  const handleForward = (email) => {
    setForwardEmail(email);
    setComposingEmail(true);
    if (editorRef.current) {
      editorRef.current.setContent(
        `<br><br>-------- Forwarded Message --------<br>` +
          `<strong>From:</strong> ${email.from_name} &lt;${email.from_email}&gt;<br>` +
          `<strong>Date:</strong> ${new Date(
            email.sent_date
          ).toLocaleString()}<br>` +
          `<strong>Subject:</strong> ${email.subject}<br>` +
          `<strong>To:</strong> ${email.to_recipients
            .map((r) => r.address)
            .join(", ")}<br>` +
          (email.cc_recipients.length
            ? `<strong>Cc:</strong> ${email.cc_recipients
                .map((r) => r.address)
                .join(", ")}<br>`
            : "") +
          `<br>${email.body_content}`
      );
    }
  };

  const scrollToBottom = () => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    if (composingEmail || replyToEmail || forwardEmail) {
      setTimeout(scrollToBottom, 100);
    }
  }, [composingEmail, replyToEmail, forwardEmail]);

  return (
    <div className="mt-5">
      <div className="container-fluid">
        <Row className="g-3">
          <div className="col-md-12">
            <div className="row justify-content-between">
              {/* Left sidebar - Email list */}
              <div className="col-md-3 mt-3 px-3 px-md-0">
                {/* <EmailList
                  emails={emails}
                  loading={loading.emails}
                  selectedEmail={selectedEmail}
                  onSelectEmail={handleSelectEmail}
                  onCompose={() => {
                    setComposingEmail(true);
                    setReplyToEmail(null);
                    setForwardEmail(null);
                  }}
                  t={t}
                /> */}
                <Moments
                  meetingsData={emails}
                  onMomentSelect={handleSelectEmail}
                  selectedMoment={selectedEmail}
                />
              </div>

              {/* Center - Email content */}
              <div className="col-md-6 mt-3 px-3 px-md-2">
                <div className="card rounded-0 h-100">
                  <div
                    className="card-body d-flex flex-column"
                    style={{ minHeight: "70vh" }}
                  >
                    {selectedEmail && (
                      <div className="moment-header mb-3 border-bottom pb-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <h5 className="mb-1">{selectedEmail.title}</h5>
                          <Link
                            to={
                              selectedEmail?.status === "closed" ||
                              selectedEmail?.status === "abort"
                                ? `/present/invite/${selectedEmail?.id}`
                                : `/invite/${selectedEmail?.id}`
                            } // Replace with your actual route path
                            className="btn btn-sm btn-outline-primary"
                            style={{
                              fontSize: "0.8rem",
                              padding: "0.25rem 0.5rem",
                            }}
                          >
                            {t("View")}{" "}
                            {/* You can change the text as needed */}
                          </Link>
                        </div>
                      </div>
                    )}

                    <div
                      className="message-container flex-grow-1 mb-3"
                      ref={messageContainerRef}
                      style={{
                        overflowY: "auto",
                        maxHeight: "calc(70vh - 200px)",
                        scrollBehavior: "smooth",
                      }}
                    >
                      {loading.emails ? (
                        <div className="d-flex justify-content-center align-items-center h-100">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      ) : emails.length > 0 ? //   {messages // <>
                      //     .filter((msg) => msg.is_pinned)
                      //     .map((message) => (
                      //       <MessageItem
                      //         key={message.id}
                      //         message={message}
                      //         onEdit={handleEditMessage}
                      //         onDelete={handleDeleteMessage}
                      //         onPin={handlePinMessage}
                      //         onShowAction={handleShowAction}
                      //         isEditing={editingMessage?.id === message.id}
                      //         currentUserId={parseInt(
                      //           sessionStorage.getItem("user_id")
                      //         )}
                      //       />
                      //     ))}
                      //   {messages
                      //     .filter((msg) => !msg.is_pinned)
                      //     .map((message) => (
                      //       <MessageItem
                      //         key={message.id}
                      //         message={message}
                      //         onEdit={handleEditMessage}
                      //         onDelete={handleDeleteMessage}
                      //         onPin={handlePinMessage}
                      //         onShowAction={handleShowAction}
                      //         isEditing={editingMessage?.id === message.id}
                      //         currentUserId={parseInt(
                      //           sessionStorage.getItem("user_id")
                      //         )}
                      //       />
                      //     ))}
                      // </>
                      null : (
                        <div className="d-flex justify-content-center align-items-center h-100">
                          <div className="text-center text-muted py-4">
                            <i className="bi bi-chat-square-text fs-1"></i>
                            <p className="mt-2">
                              {selectedEmail
                                ? t("No messages yet for this moment")
                                : t("Select a moment to view messages")}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {selectedEmail && (
                      <div className="mt-auto">
                        <MessageTypeWritter
                          // onSendMessage={handleSendMessage}
                          disabled={loading.emails}
                          editorRef={editorRef}
                          // isEditing={!!editingMessage}
                          // onCancelEdit={cancelEdit}
                          participants={selectedEmail?.user_with_participants}
                          // meeting={selected}
                          onFileUpload={handleFileUpload}
                          attachments={attachments}
                          onRemoveAttachment={removeAttachment}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right sidebar - Email details */}
              <div className="col-md-3 mt-3 px-3 px-md-2">
                <DiscussionParticipant
                  // meetingId={meetingId}
                  selectedMoment={selectedEmail}
                />
                {/* <EmailRecipients email={selectedEmail} t={t} /> */}
              </div>
            </div>
          </div>
        </Row>
      </div>
    </div>
  );
}

// Sub-components
const EmailList = ({
  emails,
  loading,
  selectedEmail,
  onSelectEmail,
  onCompose,
  t,
}) => (
  <Card className="h-100 border-0 shadow-sm">
    <Card.Header className="d-flex justify-content-between align-items-center bg-light">
      <Card.Title className="mb-0">{t("Inbox")}</Card.Title>
      <Button variant="primary" size="sm" onClick={onCompose}>
        {t("New Email")}
      </Button>
    </Card.Header>
    <Card.Body
      className="p-0"
      style={{ overflowY: "auto", overflowX: "hidden", maxHeight: "70vh" }}
    >
      {loading ? (
        <div className="d-flex justify-content-center align-items-center h-100">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : emails.length > 0 ? (
        emails.map((email) => (
          <EmailListItem
            key={email.id}
            email={email}
            isSelected={selectedEmail?.id === email.id}
            onSelect={onSelectEmail}
          />
        ))
      ) : (
        <div className="text-center text-muted py-4">
          <MailOutlined style={{ fontSize: "48px", color: "#6c757d" }} />
          <p className="mt-2">{t("No emails found")}</p>
        </div>
      )}
    </Card.Body>
  </Card>
);

const EmailListItem = ({ email, isSelected, onSelect }) => (
  <div
    className={`p-3 border-bottom cursor-pointer ${
      isSelected ? "bg-light" : ""
    } ${!email.is_read ? "fw-bold" : ""}`}
    onClick={() => onSelect(email)}
    style={{
      transition: "background-color 0.2s ease-in-out",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
    }}
    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
    onMouseLeave={(e) =>
      (e.currentTarget.style.backgroundColor = isSelected ? "#e9ecef" : "white")
    }
  >
    <div className="d-flex justify-content-between align-items-start">
      <div className="flex-grow-1" style={{ overflow: "hidden" }}>
        <div className="d-flex justify-content-between">
          <span
            className="text-truncate me-2"
            style={{
              maxWidth: "70%",
              display: "inline-block",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={email.from_email}
          >
            {email.from_name || email.from_email}
          </span>
          <small className="text-muted text-nowrap">
            {new Date(email.sent_date).toLocaleDateString()}
          </small>
        </div>
        <div
          className="text-truncate"
          style={{ overflow: "hidden", textOverflow: "ellipsis" }}
          title={email.subject}
        >
          {email.subject}
        </div>
      </div>
      {email.has_attachments && (
        <PaperClipOutlined style={{ color: "#6c757d" }} />
      )}
    </div>
  </div>
);

const EmailDetail = ({ email, onReply, onReplyAll, onForward, t }) => {
  if (!email) return null;

  const renderAttachment = (attachment) => {
    const fileUrl = `${Assets_URL}/${attachment?.path}`;
    return (
      <div key={attachment.id} className="p-2 border rounded mb-2 bg-light">
        <div className="d-flex align-items-center">
          <PaperClipOutlined className="me-2" />
          <div className="flex-grow-1">
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none text-primary"
            >
              <div>{attachment.name}</div>
              <small className="text-muted">
                {(attachment.size / 1024).toFixed(2)} KB
              </small>
            </a>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-3">
      <div className="border-bottom pb-3 mb-3">
        <h4 className="mb-3">{email.subject}</h4>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <strong>{t("From:")}</strong> {email.from_name} &lt;
            {email.from_email}&gt;
          </div>
          <small className="text-muted">
            {new Date(email.sent_date).toLocaleString()}
          </small>
        </div>
        <div>
          <strong>{t("To:")}</strong>{" "}
          {email.to_recipients.map((r) => r.address).join(", ")}
        </div>
        {email.cc_recipients.length > 0 && (
          <div>
            <strong>{t("Cc:")}</strong>{" "}
            {email.cc_recipients.map((r) => r.address).join(", ")}
          </div>
        )}
        {email.bcc_recipients.length > 0 && (
          <div>
            <strong>{t("Bcc:")}</strong>{" "}
            {email.bcc_recipients.map((r) => r.address).join(", ")}
          </div>
        )}
        <div className="mt-3 d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            className="d-flex align-items-center gap-1"
            onClick={() => onReply(email)}
          >
            <FaReply /> {t("Reply")}
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            className="d-flex align-items-center gap-1"
            onClick={() => onReplyAll(email, true)}
          >
            <FaReplyAll /> {t("Reply All")}
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            className="d-flex align-items-center gap-1"
            onClick={() => onForward(email)}
          >
            <FaShare /> {t("Forward")}
          </Button>
        </div>
      </div>
      <div dangerouslySetInnerHTML={{ __html: email.body_content }} />
      {email.has_attachments &&
        email.attachments &&
        email.attachments.length > 0 && (
          <div className="mt-4">
            <h6>{t("Attachments")}</h6>
            <div className="d-flex flex-column gap-2">
              {email.attachments.map(renderAttachment)}
            </div>
          </div>
        )}
    </div>
  );
};

const EmailComposer = ({
  onSend,
  onCancel,
  replyTo,
  forward,
  onFileUpload,
  attachments,
  onRemoveAttachment,
  editorRef,
  t,
}) => {
  const [recipients, setRecipients] = useState({
    to: replyTo
      ? replyTo.from_email
      : forward
      ? forward.to_recipients.map((r) => r.address).join(", ")
      : "",
    cc: replyTo?.replyAll
      ? replyTo.cc_recipients.map((r) => r.address).join(", ")
      : "",
    bcc: "",
    subject: replyTo
      ? `Re: ${replyTo.subject}`
      : forward
      ? `Fwd: ${forward.subject}`
      : "",
  });

  // Basic email validation
  const validateEmails = (emailString) => {
    if (!emailString) return false; // Require at least one email in 'to' field
    const emails = emailString
      .split(",")
      .map((e) => e.trim())
      .filter((e) => e);
    if (emails.length === 0) return false; // Ensure 'to' field isn't empty after trimming
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emails.every((email) => emailRegex.test(email));
  };

  const handleRecipientChange = (e) => {
    const { name, value } = e.target;
    setRecipients((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (messageContent) => {
    const { to, cc, bcc } = recipients;
    if (!validateEmails(to)) {
      message.error(
        t("Please enter at least one valid email address in To field")
      );
      return;
    }
    if (cc && !validateEmails(cc)) {
      message.error(t("Invalid email address in Cc field"));
      return;
    }
    if (bcc && !validateEmails(bcc)) {
      message.error(t("Invalid email address in Bcc field"));
      return;
    }
    onSend({
      ...recipients,
      message: messageContent.message,
    });
  };

  return (
    <div className="p-3 bg-white border rounded shadow-sm mb-3">
      <Form>
        <Form.Group className="mb-2">
          <Input
            prefix={<UserOutlined />}
            placeholder={t("To (separate multiple emails with commas)")}
            name="to"
            value={recipients.to}
            onChange={handleRecipientChange}
            style={{ borderRadius: "4px", width: "100%" }}
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Input
            prefix={<UserOutlined />}
            placeholder={t("Cc (separate multiple emails with commas)")}
            name="cc"
            value={recipients.cc}
            onChange={handleRecipientChange}
            style={{ borderRadius: "4px", width: "100%" }}
          />
        </Form.Group>
        <Form.Group className="mb-2">
          <Input
            prefix={<UserOutlined />}
            placeholder={t("Bcc (separate multiple emails with commas)")}
            name="bcc"
            value={recipients.bcc}
            onChange={handleRecipientChange}
            style={{ borderRadius: "4px", width: "100%" }}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Input
            placeholder={t("Subject")}
            name="subject"
            value={recipients.subject}
            onChange={handleRecipientChange}
            style={{ borderRadius: "4px", width: "100%" }}
          />
        </Form.Group>
        <Upload
          beforeUpload={onFileUpload}
          fileList={attachments}
          onRemove={(file) => onRemoveAttachment(attachments.indexOf(file))}
          multiple
        >
          <AntButton icon={<UploadOutlined />}>{t("Attach File")}</AntButton>
        </Upload>
        <MessageTypeWritter
          onSendMessage={handleSubmit}
          editorRef={editorRef}
          onCancel={onCancel}
          onFileUpload={onFileUpload}
          attachments={attachments}
          onRemoveAttachment={onRemoveAttachment}
          showCancelButton={true}
        />
        <div className="mt-3 d-flex gap-2">
          <AntButton
            type="primary"
            onClick={() => {
              const content = editorRef.current?.getContent();
              if (content) handleSubmit({ message: content });
              else message.error(t("Please enter email content"));
            }}
          >
            {t("Send")}
          </AntButton>
          <AntButton onClick={onCancel}>{t("Cancel")}</AntButton>
        </div>
      </Form>
    </div>
  );
};

const EmailRecipients = ({ email, t }) => (
  <Card className="h-100 border-0 shadow-sm">
    <Card.Header className="bg-light">
      <Card.Title className="mb-0">{t("Email Details")}</Card.Title>
    </Card.Header>
    <Card.Body>
      {email ? (
        <div>
          <h6>{t("Recipients")}</h6>
          <div className="mb-3">
            <strong>{t("To:")}</strong>
            <div className="text-muted">
              {email.to_recipients.map((r) => r.address).join(", ")}
            </div>
          </div>
          {email.cc_recipients.length > 0 && (
            <div className="mb-3">
              <strong>{t("Cc:")}</strong>
              <div className="text-muted">
                {email.cc_recipients.map((r) => r.address).join(", ")}
              </div>
            </div>
          )}
          {email.bcc_recipients.length > 0 && (
            <div className="mb-3">
              <strong>{t("Bcc:")}</strong>
              <div className="text-muted">
                {email.bcc_recipients.map((r) => r.address).join(", ")}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center text-muted py-4">
          <UserOutlined style={{ fontSize: "48px", color: "#6c757d" }} />
          <p className="mt-2">{t("Select an email to view details")}</p>
        </div>
      )}
    </Card.Body>
  </Card>
);

export default OutlookChat;
